import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface EnhancedComment {
  id: string;
  message: string;
  sender_id: string;
  department_id: string;
  entity_type: string;
  entity_id: string;
  priority: string | null;
  status: string | null;
  created_at: string;
  attachments: any[];
  parent_id: string | null;
  reply_count: number;
  sender?: {
    id: string;
    full_name: string | null;
    email: string | null;
  };
}

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
}

export const useEnhancedComments = (entityId: string, entityType: string) => {
  const queryClient = useQueryClient();

  const { data: comments = [], isLoading, refetch } = useQuery({
    queryKey: ["enhanced-comments", entityId, entityType],
    queryFn: async () => {
      // Fetch only top-level comments (no parent)
      const { data, error } = await supabase
        .from("inter_departmental_messages")
        .select(`
          id,
          message,
          sender_id,
          department_id,
          entity_type,
          entity_id,
          priority,
          status,
          created_at,
          attachments,
          parent_id,
          reply_count
        `)
        .eq("entity_id", entityId)
        .eq("entity_type", entityType)
        .is("parent_id", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch sender profiles
      const senderIds = [...new Set(data?.map(c => c.sender_id) || [])];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", senderIds);

      const profileMap = new Map((profiles || []).map(p => [p.id, p]));

      return data?.map(c => ({
        ...c,
        attachments: (c.attachments as any[]) || [],
        reply_count: c.reply_count || 0,
        sender: profileMap.get(c.sender_id),
      })) as EnhancedComment[];
    },
    enabled: !!entityId && !!entityType,
  });

  const { data: profiles = [] } = useQuery<Profile[]>({
    queryKey: ["all-profiles-for-mentions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .order("full_name");

      if (error) throw error;
      return (data || []) as Profile[];
    },
  });

  const { mutateAsync: addComment, isPending: isAddingComment } = useMutation({
    mutationFn: async ({ 
      message, 
      attachments = [],
      mentionedUserIds = []
    }: { 
      message: string; 
      attachments?: any[];
      mentionedUserIds?: string[];
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");

      // Get the default department (Sales for leads)
      const { data: dept } = await supabase
        .from("departments")
        .select("id")
        .eq("slug", "sales")
        .single();

      const departmentId = dept?.id || "";

      const { data, error } = await supabase
        .from("inter_departmental_messages")
        .insert({
          message,
          sender_id: session.session.user.id,
          entity_type: entityType,
          entity_id: entityId,
          department_id: departmentId,
          attachments,
          parent_id: null,
          reply_count: 0,
        })
        .select()
        .single();

      if (error) throw error;

      // Parse @mentions from message text
      const mentionPattern = /@([A-Za-z\s]+?)(?=\s@|[.,!?;:\s]*$|[^a-zA-Z\s])/g;
      const mentionsInText: string[] = [];
      let match;
      while ((match = mentionPattern.exec(message)) !== null) {
        mentionsInText.push(match[1].trim().toLowerCase());
      }

      // Match mentioned names to profile IDs
      const mentionedUserIdsFromText = profiles
        .filter(p => {
          const name = (p.full_name || "").toLowerCase();
          return mentionsInText.some(mention => name === mention);
        })
        .map(p => p.id)
        .filter(id => id !== session.session.user.id);

      // Combine explicit selections with parsed mentions (deduplicated), excluding sender
      const allMentionedUserIds = [...new Set([
        ...mentionedUserIds.filter(id => id !== session.session.user.id),
        ...mentionedUserIdsFromText
      ])];

      // Create notifications for mentioned users
      if (allMentionedUserIds.length > 0) {
        const { data: senderProfile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", session.session.user.id)
          .single();

        const senderName = senderProfile?.full_name || "Someone";

        const notifications = allMentionedUserIds.map(userId => ({
          user_id: userId,
          title: `${senderName} mentioned you`,
          message: message.length > 100 ? message.substring(0, 100) + "..." : message,
          type: "mention",
          related_entity_type: entityType,
          related_entity_id: entityId,
          is_read: false,
        }));

        await supabase.from("dashboard_notifications").insert(notifications);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enhanced-comments", entityId, entityType] });
      toast.success("Comment added");
    },
    onError: (error) => {
      toast.error("Failed to add comment");
      console.error("Error adding comment:", error);
    },
  });

  return {
    comments,
    isLoading,
    refetch,
    addComment,
    isAddingComment,
    profiles,
  };
};
